import { LAYOUT_DEPTH } from '@constants/common.const';
import { LayoutAsset } from '@models/game-config.interface';
import { GameConfigService } from '@services/core/GameConfigService';
import { LayoutService } from '@services/ui/LayoutService';
import Phaser from 'phaser';

const TEMP_UUID = 'new-asset';

export class GameBackground {
  private readonly scene: Phaser.Scene;
  private layout!: LayoutService;
  private worldHeight: number = 0;
  private worldDecor!: Phaser.GameObjects.Container; // scrolls with camera

  private bgCleanup?: () => void;
  private onAdminMessage?: (event: MessageEvent) => void;
  private decorImageByUuid = new Map<string, Phaser.GameObjects.Image>();
  private decorUrlByUuid = new Map<string, string>();
  private isAdmin = false;
  private isDraggingLocked = false;

  constructor(scene: Phaser.Scene, layoutService: LayoutService) {
    this.scene = scene;
    this.layout = layoutService;
  }

  create(): void {
    const { cleanup } = this.layout.createGameBackground(this.scene);
    this.bgCleanup = cleanup;

    // World decorations container (world space)
    this.worldDecor = this.scene.add
      .container(0, 0)
      .setScrollFactor(1) // default, but explicit
      .setDepth(LAYOUT_DEPTH + 1); // above the background

    const sessionParams = new GameConfigService().sessionParams;
    this.isAdmin = !!sessionParams?.isAdmin;
    // listen to changes only if there is an admin as a user
    if (this.isAdmin) {
      this.listenToAdminChanges();
    }
  }

  setDecorations(worldHeight: number): void {
    this.worldHeight = worldHeight;
    const decorations = this.getDecorationsSettings();
    this.setDecor(decorations);
  }

  getDecorationsSettings(): LayoutAsset[] {
    const decorations = new GameConfigService().layoutAssets;
    const decorationCount = decorations.length;
    if (decorationCount === 0) return [];

    return decorations;
  }

  // Add decoration sprites positioned using percentage coordinates
  setDecor(assets: LayoutAsset[]): void {
    if (!this.worldDecor) return;
    this.decorImageByUuid.clear();
    this.decorUrlByUuid.clear();

    // Clear existing
    this.worldDecor.removeAll(true);

    assets.forEach((a) => {
      const { x, y } = this.getCoordinates(a);

      const img = this.scene.add.image(x, y, a.url);
      if (a.scale !== undefined) img.setScale(a.scale);
      if (a.flipX !== undefined) img.setFlipX(a.flipX);
      // Store depth as custom data — container children ignore setDepth(),
      // render order is determined by child list position instead.
      img.setData({ depth: a.depth ?? 0 });

      // Add to container
      this.worldDecor.add(img);
      this.decorImageByUuid.set(a.uuid, img);
      if (a.url) this.decorUrlByUuid.set(a.uuid, a.url);

      if (this.isAdmin) this.makeDecorDraggable(img, a.uuid);
    });

    this.sortDecorByDepth();
  }

  private sortDecorByDepth(): void {
    // sort images inside container based on their depth data
    // container's children ignore setDepth(), so we need to sort manually
    this.worldDecor.sort(
      'data.values.depth',
      (childA: Phaser.GameObjects.Image, childB: Phaser.GameObjects.Image) => {
        return childA.getData('depth') - childB.getData('depth');
      },
    );
  }

  destroy(): void {
    if (this.onAdminMessage) {
      window.removeEventListener('message', this.onAdminMessage);
    }
    this.worldDecor?.destroy(true);
    this.bgCleanup?.();
    this.decorImageByUuid.clear();
    this.decorUrlByUuid.clear();
  }

  // ADMIN FEATURES (AVAILABLE ONLY FROM MANDO)
  private listenToAdminChanges(): void {
    const backofficeUrl = import.meta.env.VITE_MANDO_URL;

    this.onAdminMessage = (event) => {
      if (event.origin !== backofficeUrl || !event.data) return; // security
      try {
        const { layoutAsset, action } = JSON.parse(event.data) as {
          layoutAsset?: LayoutAsset;
          action: 'add' | 'remove' | 'update' | 'lock' | 'unlock';
        };
        if (action === 'lock' || action === 'unlock') {
          this.isDraggingLocked = action === 'lock';
          const cursor = this.isDraggingLocked ? 'default' : 'grab';
          this.decorImageByUuid.forEach((img) => {
            if (img.input) img.input.cursor = cursor;
          });
          return;
        }
        if (!layoutAsset || !layoutAsset.url) return;
        const existingDecor =
          this.decorImageByUuid.get(layoutAsset.uuid) ?? null;
        if (existingDecor) {
          this.updateExistingDecoration(
            existingDecor,
            layoutAsset,
            action as 'add' | 'remove' | 'update',
          );
        } else {
          this.createNewDecoration(layoutAsset);
        }
      } catch (e) {
        console.error('Parsing data failure', e);
      }
    };

    window.addEventListener('message', this.onAdminMessage);
  }

  private updateExistingDecoration(
    existingDecor: Phaser.GameObjects.Image,
    layoutAsset: LayoutAsset,
    action: 'add' | 'remove' | 'update' = 'update',
  ): void {
    if (action === 'remove') {
      existingDecor.destroy();
      this.worldDecor.remove(existingDecor);
      this.decorImageByUuid.delete(layoutAsset.uuid);
      this.decorUrlByUuid.delete(layoutAsset.uuid);
      return;
    }

    const currentUrl = this.decorUrlByUuid.get(layoutAsset.uuid);
    if (layoutAsset.url && layoutAsset.url !== currentUrl) {
      // URL changed — apply the new texture once it is ready.
      // Texture key = URL so multiple assets sharing the same image reuse the cache entry
      // and we never remove a URL-keyed texture (it may be shared by other assets).
      const applyNewTexture = () => {
        existingDecor.setTexture(layoutAsset.url);
        this.updateDecorSettings(layoutAsset, existingDecor);
        this.decorUrlByUuid.set(layoutAsset.uuid, layoutAsset.url);
      };
      if (this.scene.textures.exists(layoutAsset.url)) {
        applyNewTexture();
      } else {
        this.scene.load.image(layoutAsset.url, layoutAsset.url);
        this.scene.load.once('complete', applyNewTexture);
        this.scene.load.start();
      }
      return;
    }
    this.updateDecorSettings(layoutAsset, existingDecor);
  }

  private updateDecorSettings(
    newSettings: LayoutAsset,
    decor: Phaser.GameObjects.Image,
  ) {
    const { x, y } = this.getCoordinates(newSettings);
    decor.setPosition(x, y);
    this.updateSecondarySettings(newSettings, decor);
  }

  private updateSecondarySettings(
    newSettings: LayoutAsset,
    decor: Phaser.GameObjects.Image,
  ): void {
    if (newSettings.scale !== undefined) decor.setScale(newSettings.scale);
    if (newSettings.flipX !== undefined) decor.setFlipX(newSettings.flipX);
    if (newSettings.depth !== undefined) {
      decor.setData('depth', newSettings.depth);
      this.sortDecorByDepth();
    }
  }

  private createNewDecoration(layoutAsset: LayoutAsset): void {
    if (layoutAsset.uuid === TEMP_UUID) {
      const addToScene = () => {
        const cam = this.scene.cameras.main;
        const x = cam.scrollX + cam.width / 2;
        const y = cam.scrollY + cam.height / 2;
        const img = this.scene.add.image(x, y, layoutAsset.url);
        this.updateSecondarySettings(layoutAsset, img);
        this.decorImageByUuid.set(layoutAsset.uuid, img);
        this.decorUrlByUuid.set(layoutAsset.uuid, layoutAsset.url);
        this.makeDecorDraggable(img, layoutAsset.uuid);
        this.sendCoordinatesToBackoffice(TEMP_UUID, x, y);
      };
      if (this.scene.textures.exists(layoutAsset.url)) {
        addToScene();
      } else {
        this.scene.load.image(layoutAsset.url, layoutAsset.url);
        this.scene.load.once('complete', addToScene);
        this.scene.load.start();
      }
    } else {
      // Replace the temporary uuid (TEMP_UUID) with the real one
      const tempDecor = this.decorImageByUuid.get(TEMP_UUID);
      if (tempDecor) {
        this.decorImageByUuid.delete(TEMP_UUID);
        this.decorImageByUuid.set(layoutAsset.uuid, tempDecor);
        this.decorUrlByUuid.delete(TEMP_UUID);
        // Point the image at the confirmed URL. URL-keyed textures are never removed
        // because other assets may share the same cache entry.
        const applyFinalTexture = () => {
          tempDecor.setTexture(layoutAsset.url);
          this.decorUrlByUuid.set(layoutAsset.uuid, layoutAsset.url);
        };
        if (this.scene.textures.exists(layoutAsset.url)) {
          applyFinalTexture();
        } else {
          this.scene.load.image(layoutAsset.url, layoutAsset.url);
          this.scene.load.once('complete', applyFinalTexture);
          this.scene.load.start();
        }
      }
    }
  }

  private makeDecorDraggable(
    img: Phaser.GameObjects.Image,
    uuid: string,
  ): void {
    img.setInteractive({ cursor: this.isDraggingLocked ? 'default' : 'grab' });
    this.scene.input.setDraggable(img);

    img.on(Phaser.Input.Events.DRAG_START, () => {
      if (this.isDraggingLocked) return;
      this.scene.data.set('decorDragging', true);
    });

    img.on(
      Phaser.Input.Events.DRAG,
      (_pointer: Phaser.Input.Pointer, dragX: number, dragY: number) => {
        if (this.isDraggingLocked) return;
        // dragX/dragY are world-space coords. Container is at (0,0) so these
        // are identical to local container coords.
        img.setPosition(dragX, dragY);
      },
    );

    img.on(Phaser.Input.Events.DRAG_END, () => {
      if (this.isDraggingLocked) return;
      this.scene.data.set('decorDragging', false);
      this.sendCoordinatesToBackoffice(uuid, img.x, img.y);
    });
  }

  private sendCoordinatesToBackoffice(
    uuid: string,
    x: number,
    y: number,
  ): void {
    const xPercent = (x / this.layout.screenWidth) * 100;
    const yPercent = (y / this.worldHeight) * 100;

    const backofficeUrl = import.meta.env.VITE_MANDO_URL;
    window.parent.postMessage(
      JSON.stringify({ uuid, x: xPercent, y: yPercent }),
      backofficeUrl,
    );
  }

  private getCoordinates(asset: LayoutAsset): { x: number; y: number } {
    // Convert percentage coordinates to real pixel positions
    const realX = (asset.x / 100) * this.layout.screenWidth;
    const realY = (asset.y / 100) * this.worldHeight;
    return { x: realX, y: realY };
  }
}
